import React, { useEffect, useState } from "react";
import logo from "./logo.svg";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  backdropClasses,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Divider,
  Fade,
  Grow,
  Icon,
  IconButton,
  Paper,
  TextField,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { gcTheme } from "./Themes";
import Main from "./Main";
import {
  SnackbarProvider,
  VariantType,
  enqueueSnackbar,
  useSnackbar,
} from "notistack";
import VideoPlayer from "./VideoPlayer";
import { ACTIONS, GCState, IS_DEV as IS_DEVELOPMENT_MODE } from "./states";
import Device from "./Device";
import ConnectedDevice from "./ConnectedDevice";
import { ArrowDownward, ArrowDropDown, Close } from "@mui/icons-material";

import background from "./images/bg.png";
import { Colors, glassColor, whiteGlassColor } from "./Colors";
import { BrowserView, MobileView } from "react-device-detect";

import * as DeviceDetect from "react-device-detect";
import { useSmallScreen } from "./ScreenHook";

import { VERSION_NUMBER, BUILD_TIMESTAMP } from "./build_timestamp"

//This is the main class for the app
//It functions as the entry point for the app
//handles the connected devices, and allows the user to connect to a headest
function App() {
  //Are we connected to a headset?
  const [connected, setConnected] = useState(false);

  //The connection id entered by the user, DOES NOT ALWAYS MATCH PROPERLY
  //if the user enters a nickname, it will be used to find the connection id
  //if the connection id doesnt match a found headset, it will fail to connect
  const [myConnectionId, setMyConnectionId] = useState<string>("");

  //the value entered by the user in the text field
  const [entered, setEntered] = useState("");

  //a list of devices that have been connected to in the past
  const [devices, setDevices] = useState<Device[]>([]);

  const [isAborted, setIsAborted] = useState(false);
  const [abortedReason, setAbortedReason] = useState("");

  const [isMobile, setIsMobile] = useState(
    useSmallScreen() || DeviceDetect.isMobile
  );

  //this loads the previously connected devices from local storage
  //and sets the devices state to the loaded devices
  useEffect(() => {
    var connectedDevices: Device[] = JSON.parse(
      localStorage.getItem("connectedDevices") || "[]"
    );
    setDevices(connectedDevices);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("aborted") === "true") {
      setIsAborted(true);
      const reason = urlParams.get("reason");
      if (reason) {
        setAbortedReason(reason);
      }
    }
  }, []);

  //this function returns a list of the previously connected devices
  //It displays them each in an exapandable list

  let mappedDevices = () => {
    return (
      <>
        {devices.map((device) => {
          return (
            <ConnectedDevice
              connectionId={device.connectionId}
              nickname={device.nickname}
              onClick={() => {
                setMyConnectionId(device.connectionId);
                setConnected(true);
              }}
              setDevices={setDevices}
            />
          );
        })}
      </>
    );
  };

  let getPreviouslyConnectedDevices = () => {
    if (devices.length > 0) {
      return (
        <Accordion
          sx={{
            width: "80%",
            backgroundColor: whiteGlassColor(),
            backdropFilter: "blur(15px)",
          }}
        >
          <AccordionSummary
            expandIcon={<ArrowDropDown />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            {/* <Chip label="Previously Connected Devices" /> */}
            <Typography variant="h6">Previously Connected Devices</Typography>
          </AccordionSummary>
          <AccordionDetails>{mappedDevices()}</AccordionDetails>
        </Accordion>
      );
    }
  };

  function modifyURL() {
    if (isAborted) {
      window.history.replaceState(
        {},
        window.location.pathname,
        "?aborted=false"
      );
      setIsAborted(false);
    }
  }
  
  useEffect(() => {
    // Check if the device is mobile and update the state
    // eslint-disable-next-line react-hooks/rules-of-hooks
    setIsMobile(window.innerWidth < 768 || DeviceDetect.isMobile);
  }, []);

  return (
    <ThemeProvider theme={gcTheme}>
      {!connected ? (
        <Paper
          sx={{
            position: "absolute",
            bottom: "0.5em",
            left: "0.5em",
            padding: 1,
            backgroundColor: "#27292a",
            zIndex: 1000,
          }}
        >
          {IS_DEVELOPMENT_MODE ? (
            <Typography fontSize={14} color="success" fontWeight="bold">
              DEV MODE ENABLED
            </Typography>
          ) : null}
          <Typography fontSize={14} color="secondary" fontWeight={"bold"}>
            Version {VERSION_NUMBER} - {BUILD_TIMESTAMP}
          </Typography>
        </Paper>
      ) : null}
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          alignContent: "center",
          alignItems: "center",
          justifyContent: "center",
          justifyItems: "center",
          width: "100%",
          backgroundColor: "black",
        }}
        sx={{
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            filter: "blur(8px)",
            backgroundImage: connected ? "none" : `url(${background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          },
        }}
      >
        <SnackbarProvider maxSnack={10}>
          {!connected ? (
            <>
              {isAborted ? (
                <Grow in={isAborted}>
                  <Alert
                  variant="filled"
                  severity="warning"
                  sx={{ zIndex: 1000, marginBottom: 1, display: "flex", alignItems: "center" }}
                >
                  {abortedReason || "Groove Catcher disconnected unexpectedly."}
                  <IconButton
                    onClick={() => {
                      setIsAborted(false);
                      setAbortedReason("");
                      modifyURL();
                    }}
                  >
                    <Close color="action" />
                  </IconButton>
                </Alert>
                </Grow>
                
              ) : null}
              <Paper
                style={{
                  width: isMobile ? "80%" :"50%",
                  height: isMobile ? "50%" : "auto",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  //space between items
                  paddingTop: isMobile ? "1em" : "6em",
                  paddingBottom: isMobile ? "1em" : "6em",
                  paddingLeft: "1em",
                  paddingRight: "1em",
                }}
                sx={{
                  padding: 2,
                  backgroundColor: whiteGlassColor(true, 0.3),
                  backdropFilter: "blur(15px)",
                  borderRadius: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    alignContent: "center",

                    width: "100%",
                  }}
                >
                  <Typography
                    variant={isMobile ? "h5" : "h3"}
                    zIndex={1000}
                    fontWeight={"bold"}
                    color="textPrimary"
                    textAlign={isMobile ? "center" : "left"}
                  >
                    Groove Catcher Remote Control
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <TextField
                      label="Headset ID/Nickname"
                      placeholder="enter id/nickname"
                      fullWidth={true}
                      style={{ width: isMobile ? "100%" : "80%" }}
                      onChange={(e) => {
                        setEntered(e.target.value.toLowerCase().replaceAll(" ", "")); 
                      }}
                    />
                    <Button
                      variant="contained"
                      sx={{ m: 1, marginLeft: -14 }}
                      onClick={() => {
                        modifyURL();

                        var connectedDevices: Device[] = JSON.parse(
                          localStorage.getItem("connectedDevices") || "[]"
                        );
                        var isConnectionId = connectedDevices.find(
                          (device) => device.connectionId === entered
                        );
                        var isNickname = connectedDevices.find(
                          (device) => device.nickname === entered
                        );
                        if (isConnectionId) {
                          setMyConnectionId(isConnectionId.connectionId);
                        } else if (isNickname) {
                          setMyConnectionId(isNickname.connectionId);
                        } else {
                          setMyConnectionId(entered);
                        }
                        setConnected(true);
                      }}
                    >
                      Connect
                    </Button>
                  </div>
                  <BrowserView style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    overflowY: "auto",
                    overflowX: "hidden",
                    marginTop: "1em",
                  }}>{getPreviouslyConnectedDevices()}</BrowserView>
                </div>
                <MobileView style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <Typography variant="h6" fontWeight={"bold"} color="textPrimary">
                      Recent Devices
                    </Typography>
                  <Paper sx={{
                    margin: 1,
                    padding: 2,
                    backgroundColor: whiteGlassColor(true, 0.3),
                    backdropFilter: "blur(15px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}>
                    
                    {mappedDevices()}
                  </Paper>
                </MobileView>
              </Paper>
            </>
          ) : (
            <Main connectionId={myConnectionId} abortState={setIsAborted} setConnected={setConnected}/>
          )}
        </SnackbarProvider>
      </Box>
    </ThemeProvider>
  );
}

export default App;
